/**
 * Pure logic of the touch reading on charts (< 768px, AD-021): which row is
 * under the finger and whether the gesture is a tap (opens the drill-down)
 * or a drag (only moves the reading).
 */

/** Area charts place rows on points, bar charts on bands. */
export type ChartScale = 'point' | 'band';

/** Plot area from `usePlotArea()`: x and width, px relative to the wrapper. */
export interface PlotSpan {
  left: number;
  width: number;
}

/**
 * Index of the row nearest to `x`, always within [0, count - 1] (a finger
 * outside the plot pins to the nearest edge). null without rows or before
 * the plot area has a width.
 */
export function nearestIndexFromX(
  x: number,
  plot: PlotSpan,
  count: number,
  scale: ChartScale
): number | null {
  if (count <= 0 || plot.width <= 0) return null;
  const offset = x - plot.left;
  const raw =
    scale === 'band'
      ? Math.floor(offset / (plot.width / count))
      : count === 1
        ? 0
        : Math.round(offset / (plot.width / (count - 1)));
  return Math.min(count - 1, Math.max(0, raw));
}

/** Moving less than this is still a tap; this much or more is a drag. */
export const TAP_SLOP_PX = 10;
/** A press this long or longer does not open the drill-down. */
export const TAP_MAX_MS = 500;

export type ScrubState =
  | { phase: 'idle' }
  | {
      phase: 'pressed' | 'scrubbing';
      startX: number;
      startedAt: number;
      index: number | null;
    };

export type ScrubEvent =
  | { type: 'down'; x: number; at: number; index: number | null }
  | { type: 'move'; x: number; index: number | null }
  | { type: 'up'; at: number }
  /** pointercancel: the page started scrolling. */
  | { type: 'cancel' };

const IDLE: ScrubState = { phase: 'idle' };

export function scrubReducer(
  state: ScrubState,
  event: ScrubEvent
): { state: ScrubState; tapIndex: number | null } {
  switch (event.type) {
    case 'down':
      return {
        state: {
          phase: 'pressed',
          startX: event.x,
          startedAt: event.at,
          index: event.index,
        },
        tapIndex: null,
      };
    case 'move': {
      if (state.phase === 'idle') return { state, tapIndex: null };
      const dragged =
        state.phase === 'scrubbing' ||
        Math.abs(event.x - state.startX) >= TAP_SLOP_PX;
      return {
        state: {
          ...state,
          phase: dragged ? 'scrubbing' : 'pressed',
          index: event.index,
        },
        tapIndex: null,
      };
    }
    case 'up':
      return {
        state: IDLE,
        tapIndex:
          state.phase === 'pressed' && event.at - state.startedAt < TAP_MAX_MS
            ? state.index
            : null,
      };
    case 'cancel':
      return { state: IDLE, tapIndex: null };
  }
}

/** Row under the finger, or null when no finger is down. */
export function activeIndexOf(state: ScrubState): number | null {
  return state.phase === 'idle' ? null : state.index;
}
